import React, { useCallback, useEffect, useState } from 'react';
import { uploadService } from '../../services/api';
import type { MountGuideDetails, MountGuideSkill } from '../../types';
import { resolvePublicMediaUrl } from '../../utils/newsContent';
import {
    BARTOLO_DETAILS,
    DEFAULT_MOUNT_DETAILS,
    emptyDrop,
    emptyMaterial,
    emptyMission,
    emptyNote,
    emptySkill,
    emptyStat,
    parseMountDetails,
    stringifyMountDetails,
} from '../../utils/mountGuideDetails';

interface Props {
    value: string;
    onChange: (json: string) => void;
}

const MountGuideMountForm: React.FC<Props> = ({ value, onChange }) => {
    const [details, setDetails] = useState<MountGuideDetails>(() => parseMountDetails(value));
    const [showJson, setShowJson] = useState(false);

    useEffect(() => {
        setDetails(parseMountDetails(value));
    }, [value]);

    const commit = useCallback(
        (next: MountGuideDetails) => {
            setDetails(next);
            onChange(stringifyMountDetails(next));
        },
        [onChange]
    );

    const patch = (partial: Partial<MountGuideDetails>) => commit({ ...details, ...partial });

    const updateList = <T,>(key: keyof MountGuideDetails, list: T[], index: number, item: T) => {
        const copy = [...(list as T[])];
        copy[index] = item;
        patch({ [key]: copy } as Partial<MountGuideDetails>);
    };

    const removeFromList = <T,>(key: keyof MountGuideDetails, list: T[], index: number) => {
        patch({ [key]: list.filter((_, i) => i !== index) } as Partial<MountGuideDetails>);
    };

    const addToList = <T,>(key: keyof MountGuideDetails, list: T[] | undefined, item: T) => {
        patch({ [key]: [...(list || []), item] } as Partial<MountGuideDetails>);
    };

    const mapPreviewUrl = resolvePublicMediaUrl(details.mapImageUrl);

    const uploadMapImage = async (file: File) => {
        const result = await uploadService.uploadImage(file);
        patch({ mapImageUrl: result.url });
    };

    const setMarkerFromClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
        patch({
            mapMarker: {
                x: Math.round(x * 100) / 100,
                y: Math.round(y * 100) / 100,
            },
        });
    };

    return (
        <div className="mount-guide-details-form">
            <p className="mount-guide-form-hint">
                Estas seções aparecem para <strong>toda montaria nova</strong> — preencha só o que precisar. O JSON é
                gerado automaticamente ao salvar.
            </p>
            <div className="mount-guide-form-toolbar">
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => commit(DEFAULT_MOUNT_DETAILS)}
                >
                    Modelo vazio
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => commit(BARTOLO_DETAILS)}>
                    Modelo Bartolo
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowJson((s) => !s)}>
                    {showJson ? 'Ocultar JSON' : 'Ver JSON'}
                </button>
            </div>

            {showJson && (
                <textarea
                    className="mount-guide-json-preview"
                    rows={8}
                    value={value}
                    readOnly
                />
            )}

            <details open className="mount-guide-form-section">
                <summary>Informações básicas (nome, nota ★, captura, níveis…)</summary>
                <div className="form-row-inline">
                    <div className="form-group">
                        <label>Grau (estrelas 1–5)</label>
                        <input
                            type="number"
                            min={1}
                            max={5}
                            value={details.grade ?? 1}
                            onChange={(e) => patch({ grade: Number(e.target.value) })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Nível monstro</label>
                        <input
                            type="number"
                            value={details.monsterLevel ?? ''}
                            onChange={(e) => patch({ monsterLevel: Number(e.target.value) })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Pontos captura</label>
                        <input
                            type="number"
                            value={details.capturePoints ?? ''}
                            onChange={(e) => patch({ capturePoints: Number(e.target.value) })}
                        />
                    </div>
                </div>
                <div className="form-row-inline">
                    <div className="form-group">
                        <label>Nível familiar (mín)</label>
                        <input
                            type="number"
                            value={details.fellowLevelMin ?? ''}
                            onChange={(e) => patch({ fellowLevelMin: Number(e.target.value) })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Nível familiar (máx)</label>
                        <input
                            type="number"
                            value={details.fellowLevelMax ?? ''}
                            onChange={(e) => patch({ fellowLevelMax: Number(e.target.value) })}
                        />
                    </div>
                </div>
                <div className="form-row-inline">
                    <div className="form-group">
                        <label>Tipo movimento PT</label>
                        <input
                            value={details.moveTypePtBr ?? ''}
                            onChange={(e) => patch({ moveTypePtBr: e.target.value })}
                            placeholder="Ar, Terrestre…"
                        />
                    </div>
                    <div className="form-group">
                        <label>Tipo movimento EN</label>
                        <input
                            value={details.moveTypeEn ?? ''}
                            onChange={(e) => patch({ moveTypeEn: e.target.value })}
                        />
                    </div>
                </div>
                <label>Condições de captura</label>
                {(details.captureConditions?.length ? details.captureConditions : ['']).map((cond, i) => (
                    <div key={i} className="form-row-inline mount-guide-list-row">
                        <input
                            value={cond}
                            onChange={(e) => {
                                const list = [...(details.captureConditions || [''])];
                                list[i] = e.target.value;
                                patch({ captureConditions: list });
                            }}
                            placeholder={`Condição ${i + 1}`}
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() =>
                                removeFromList('captureConditions', details.captureConditions || [''], i)
                            }
                        >
                            ×
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                        addToList('captureConditions', details.captureConditions, '')
                    }
                >
                    + Condição
                </button>
            </details>

            <details open className="mount-guide-form-section mount-guide-form-section-map">
                <summary>Área de aparição e mapa (onde fica o mapa no site)</summary>
                <p className="mount-guide-form-hint mount-guide-form-hint-highlight">
                    <strong>Mapa:</strong> envie a imagem abaixo. Sem ela, o mapa não aparece na página pública — só a
                    tabela de texto. No guia oficial, a coluna «Altitude de aparição» costuma trazer o texto de
                    patrulha (ex.: 80 metros).
                </p>
                <div className="form-row-inline">
                    <div className="form-group">
                        <label>Área de aparição (PT)</label>
                        <input
                            value={details.spawnAreaPtBr ?? ''}
                            onChange={(e) => patch({ spawnAreaPtBr: e.target.value })}
                        />
                    </div>
                    <div className="form-group">
                        <label>Área EN</label>
                        <input
                            value={details.spawnAreaEn ?? ''}
                            onChange={(e) => patch({ spawnAreaEn: e.target.value })}
                        />
                    </div>
                </div>
                <div className="form-group">
                    <label>Altitude de aparição (opcional — ex.: 1700 m)</label>
                    <input
                        value={details.spawnAltitude ?? ''}
                        onChange={(e) => patch({ spawnAltitude: e.target.value })}
                        placeholder="Deixe vazio se usar só patrulha abaixo"
                    />
                </div>
                <div className="form-group">
                    <label>Altitude de aparição / patrulha (PT) — 2ª coluna do guia</label>
                    <textarea
                        rows={2}
                        value={details.spawnBehaviorPtBr ?? ''}
                        onChange={(e) => patch({ spawnBehaviorPtBr: e.target.value })}
                        placeholder="Ex.: Patrulhamento em intervalos de aproximadamente 80 metros."
                    />
                </div>
                <div className="form-group mount-guide-map-upload-box">
                    <label>Imagem do mapa (obrigatório para exibir no site)</label>
                    <input
                        value={details.mapImageUrl ?? ''}
                        onChange={(e) => patch({ mapImageUrl: e.target.value })}
                        placeholder="URL ou envie arquivo abaixo"
                    />
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void uploadMapImage(file);
                            e.target.value = '';
                        }}
                    />
                </div>
                {mapPreviewUrl && (
                    <div className="mount-guide-admin-map-block">
                        <label>Preview do mapa — clique na imagem para posicionar o spawn</label>
                        <div
                            className="mount-guide-admin-map-preview"
                            onClick={setMarkerFromClick}
                            role="presentation"
                        >
                            <img src={mapPreviewUrl} alt="Mapa de spawn" />
                            <span
                                className="mount-guide-map-marker"
                                style={{
                                    left: `${(details.mapMarker?.x ?? 0.5) * 100}%`,
                                    top: `${(details.mapMarker?.y ?? 0.5) * 100}%`,
                                }}
                            />
                        </div>
                        <p className="mount-guide-form-hint">
                            Marcador: X {(details.mapMarker?.x ?? 0.5).toFixed(2)}, Y{' '}
                            {(details.mapMarker?.y ?? 0.5).toFixed(2)} (também ajustável nos campos abaixo)
                        </p>
                    </div>
                )}
                <div className="form-row-inline">
                    <div className="form-group">
                        <label>Marcador X (0–1)</label>
                        <input
                            type="number"
                            step={0.01}
                            min={0}
                            max={1}
                            value={details.mapMarker?.x ?? 0.5}
                            onChange={(e) =>
                                patch({
                                    mapMarker: {
                                        x: Number(e.target.value),
                                        y: details.mapMarker?.y ?? 0.5,
                                    },
                                })
                            }
                        />
                    </div>
                    <div className="form-group">
                        <label>Marcador Y (0–1)</label>
                        <input
                            type="number"
                            step={0.01}
                            min={0}
                            max={1}
                            value={details.mapMarker?.y ?? 0.5}
                            onChange={(e) =>
                                patch({
                                    mapMarker: {
                                        x: details.mapMarker?.x ?? 0.5,
                                        y: Number(e.target.value),
                                    },
                                })
                            }
                        />
                    </div>
                </div>
            </details>

            <details className="mount-guide-form-section">
                <summary>Notas (ex.: «desaparece após 1 hora» — aparecem após o mapa no site)</summary>
                {(details.notes || []).map((note, i) => (
                    <div key={i} className="mount-guide-form-block">
                        <select
                            value={note.variant || 'info'}
                            onChange={(e) =>
                                updateList('notes', details.notes || [], i, {
                                    ...note,
                                    variant: e.target.value as 'info' | 'warning',
                                })
                            }
                        >
                            <option value="info">Info</option>
                            <option value="warning">Aviso (vermelho)</option>
                        </select>
                        <textarea
                            rows={2}
                            placeholder="Texto PT"
                            value={note.textPtBr}
                            onChange={(e) =>
                                updateList('notes', details.notes || [], i, { ...note, textPtBr: e.target.value })
                            }
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => removeFromList('notes', details.notes || [], i)}
                        >
                            Remover nota
                        </button>
                    </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addToList('notes', details.notes, emptyNote())}>
                    + Nota
                </button>
            </details>

            <details className="mount-guide-form-section">
                <summary>Estatísticas (nível 1 / máximo)</summary>
                <table className="admin-table mount-guide-stat-editor">
                    <thead>
                        <tr>
                            <th>Chave</th>
                            <th>Label PT</th>
                            <th>Lv1</th>
                            <th>Lv máx</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {(details.stats || []).map((row, i) => (
                            <tr key={i}>
                                <td>
                                    <input
                                        value={row.key}
                                        onChange={(e) =>
                                            updateList('stats', details.stats || [], i, { ...row, key: e.target.value })
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        value={row.labelPtBr}
                                        onChange={(e) =>
                                            updateList('stats', details.stats || [], i, {
                                                ...row,
                                                labelPtBr: e.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        value={row.lv1}
                                        onChange={(e) =>
                                            updateList('stats', details.stats || [], i, { ...row, lv1: e.target.value })
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        value={row.lvMax}
                                        onChange={(e) =>
                                            updateList('stats', details.stats || [], i, {
                                                ...row,
                                                lvMax: e.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => removeFromList('stats', details.stats || [], i)}
                                    >
                                        ×
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToList('stats', details.stats, emptyStat(`stat${(details.stats?.length || 0) + 1}`))}
                >
                    + Linha de stat
                </button>
                <textarea
                    rows={2}
                    placeholder="Nota rodapé stats (PT)"
                    value={details.statsNotePtBr ?? ''}
                    onChange={(e) => patch({ statsNotePtBr: e.target.value })}
                />
            </details>

            <details className="mount-guide-form-section">
                <summary>Habilidades passivas</summary>
                {(details.skills || []).map((skill, i) => (
                    <SkillBlock
                        key={i}
                        skill={skill}
                        onChange={(s) => updateList('skills', details.skills || [], i, s)}
                        onRemove={() => removeFromList('skills', details.skills || [], i)}
                    />
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => addToList('skills', details.skills, emptySkill())}>
                    + Passiva
                </button>
            </details>

            <details className="mount-guide-form-section">
                <summary>Habilidades ativas</summary>
                {(details.activeSkills || []).map((skill, i) => (
                    <SkillBlock
                        key={i}
                        skill={skill}
                        active
                        onChange={(s) => updateList('activeSkills', details.activeSkills || [], i, s)}
                        onRemove={() => removeFromList('activeSkills', details.activeSkills || [], i)}
                    />
                ))}
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToList('activeSkills', details.activeSkills, { ...emptySkill(), skillType: 'Attack' })}
                >
                    + Ativa
                </button>
            </details>

            <details className="mount-guide-form-section">
                <summary>Pedra de selamento</summary>
                <table className="admin-table mount-guide-stat-editor">
                    <thead>
                        <tr>
                            <th>Label PT</th>
                            <th>Lv1</th>
                            <th>Lv máx</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {(details.sealStone || []).map((row, i) => (
                            <tr key={i}>
                                <td>
                                    <input
                                        value={row.labelPtBr}
                                        onChange={(e) =>
                                            updateList('sealStone', details.sealStone || [], i, {
                                                ...row,
                                                labelPtBr: e.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        value={row.lv1}
                                        onChange={(e) =>
                                            updateList('sealStone', details.sealStone || [], i, {
                                                ...row,
                                                lv1: e.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        value={row.lvMax}
                                        onChange={(e) =>
                                            updateList('sealStone', details.sealStone || [], i, {
                                                ...row,
                                                lvMax: e.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => removeFromList('sealStone', details.sealStone || [], i)}
                                    >
                                        ×
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                        addToList('sealStone', details.sealStone, {
                            key: `seal${(details.sealStone?.length || 0) + 1}`,
                            labelPtBr: '',
                            labelEn: '',
                            lv1: '',
                            lvMax: '',
                        })
                    }
                >
                    + Linha selamento
                </button>
            </details>

            <details className="mount-guide-form-section">
                <summary>Captura — materiais, drops e missões</summary>
                <h4>Materiais de produção</h4>
                {(details.captureMaterials || []).map((m, i) => (
                    <div key={i} className="form-row-inline mount-guide-list-row">
                        <input
                            placeholder="Item"
                            value={m.namePtBr}
                            onChange={(e) =>
                                updateList('captureMaterials', details.captureMaterials || [], i, {
                                    ...m,
                                    namePtBr: e.target.value,
                                })
                            }
                        />
                        <input
                            placeholder="Qtd"
                            value={m.quantity}
                            onChange={(e) =>
                                updateList('captureMaterials', details.captureMaterials || [], i, {
                                    ...m,
                                    quantity: e.target.value,
                                })
                            }
                            style={{ maxWidth: 80 }}
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => removeFromList('captureMaterials', details.captureMaterials || [], i)}
                        >
                            ×
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToList('captureMaterials', details.captureMaterials, emptyMaterial())}
                >
                    + Material
                </button>

                <h4>Drops de receita / item</h4>
                {(details.dropSources || []).map((d, i) => (
                    <div key={i} className="form-row-inline mount-guide-list-row">
                        <input
                            placeholder="Item"
                            value={d.itemPtBr}
                            onChange={(e) =>
                                updateList('dropSources', details.dropSources || [], i, {
                                    ...d,
                                    itemPtBr: e.target.value,
                                })
                            }
                        />
                        <input
                            placeholder="Monstro"
                            value={d.monsterPtBr}
                            onChange={(e) =>
                                updateList('dropSources', details.dropSources || [], i, {
                                    ...d,
                                    monsterPtBr: e.target.value,
                                })
                            }
                        />
                        <input
                            placeholder="Nv"
                            value={d.level ?? ''}
                            onChange={(e) =>
                                updateList('dropSources', details.dropSources || [], i, {
                                    ...d,
                                    level: e.target.value,
                                })
                            }
                            style={{ maxWidth: 60 }}
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => removeFromList('dropSources', details.dropSources || [], i)}
                        >
                            ×
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToList('dropSources', details.dropSources, emptyDrop())}
                >
                    + Drop
                </button>

                <h4>Missões (como obter materiais)</h4>
                {(details.missionHints || []).map((m, i) => (
                    <div key={i} className="mount-guide-form-block">
                        <input
                            placeholder="Material"
                            value={m.materialPtBr}
                            onChange={(e) =>
                                updateList('missionHints', details.missionHints || [], i, {
                                    ...m,
                                    materialPtBr: e.target.value,
                                })
                            }
                        />
                        <input
                            placeholder="Nome da missão"
                            value={m.missionPtBr}
                            onChange={(e) =>
                                updateList('missionHints', details.missionHints || [], i, {
                                    ...m,
                                    missionPtBr: e.target.value,
                                })
                            }
                        />
                        <textarea
                            rows={2}
                            placeholder="Detalhes"
                            value={m.detailsPtBr}
                            onChange={(e) =>
                                updateList('missionHints', details.missionHints || [], i, {
                                    ...m,
                                    detailsPtBr: e.target.value,
                                })
                            }
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => removeFromList('missionHints', details.missionHints || [], i)}
                        >
                            Remover
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addToList('missionHints', details.missionHints, emptyMission())}
                >
                    + Missão
                </button>
            </details>
        </div>
    );
};

const SkillBlock: React.FC<{
    skill: MountGuideSkill;
    active?: boolean;
    onChange: (s: MountGuideSkill) => void;
    onRemove: () => void;
}> = ({ skill, active, onChange, onRemove }) => {
    const [uploadingIcon, setUploadingIcon] = useState(false);
    const iconPreview = resolvePublicMediaUrl(skill.iconUrl);

    const uploadIcon = async (file: File) => {
        setUploadingIcon(true);
        try {
            const result = await uploadService.uploadImage(file);
            onChange({ ...skill, iconUrl: result.url });
        } finally {
            setUploadingIcon(false);
        }
    };

    return (
        <div className="mount-guide-form-block">
            <div className="mount-guide-skill-icon-row">
                <div className="mount-guide-skill-icon-preview-wrap">
                    {iconPreview ? (
                        <img src={iconPreview} alt="" className="mount-guide-skill-icon-preview" />
                    ) : (
                        <span className="mount-guide-skill-icon-placeholder">Ícone</span>
                    )}
                </div>
                <div className="mount-guide-skill-icon-inputs">
                    <label>Ícone da habilidade</label>
                    <input
                        value={skill.iconUrl ?? ''}
                        onChange={(e) => onChange({ ...skill, iconUrl: e.target.value })}
                        placeholder="URL do ícone"
                    />
                    <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingIcon}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void uploadIcon(file);
                            e.target.value = '';
                        }}
                    />
                    {uploadingIcon && <span className="mount-guide-form-hint">Enviando ícone…</span>}
                </div>
            </div>
            <div className="form-row-inline">
                <input
                    placeholder="Nome PT"
                    value={skill.namePtBr}
                    onChange={(e) => onChange({ ...skill, namePtBr: e.target.value })}
                />
                <input
                    type="number"
                    placeholder="Nv máx"
                    value={skill.maxLevel ?? ''}
                    onChange={(e) => onChange({ ...skill, maxLevel: Number(e.target.value) })}
                    style={{ maxWidth: 80 }}
                />
            </div>
            {active && (
                <div className="form-row-inline">
                    <input
                        placeholder="Energia"
                        value={skill.energy ?? ''}
                        onChange={(e) => onChange({ ...skill, energy: e.target.value })}
                    />
                    <input
                        placeholder="Conjuração"
                        value={skill.cast ?? ''}
                        onChange={(e) => onChange({ ...skill, cast: e.target.value })}
                    />
                    <input
                        placeholder="Recarga"
                        value={skill.cooldown ?? ''}
                        onChange={(e) => onChange({ ...skill, cooldown: e.target.value })}
                    />
                    <input
                        placeholder="Dano"
                        value={skill.damage ?? ''}
                        onChange={(e) => onChange({ ...skill, damage: e.target.value })}
                    />
                </div>
            )}
            <textarea
                rows={3}
                placeholder="Efeito PT"
                value={skill.effectPtBr ?? ''}
                onChange={(e) => onChange({ ...skill, effectPtBr: e.target.value })}
            />
            <button type="button" className="btn btn-secondary btn-sm" onClick={onRemove}>
                Remover habilidade
            </button>
        </div>
    );
};

export default MountGuideMountForm;
